/**
 * El drawer del widget de feedback: orquesta el formulario, el envío y los estados.
 *
 * ## Decisiones que no se ven en el JSX
 *
 * - **No-modal**: sin overlay, sin `body{overflow:hidden}`, sin `aria-modal`, sin focus trap duro.
 *   Es un formulario para reportar **lo que se ve detrás**; bloquear el scroll de la app justo acá
 *   sería hostil. Sí hay: foco al título al abrir, Escape para cerrar y foco devuelto al launcher.
 * - **El botón Enviar nunca se deshabilita por validación**, sólo mientras hay un request en
 *   vuelo. Un botón gris que no explica por qué está gris es el anti-patrón clásico: click con el
 *   formulario inválido → mensaje en `role="alert"` + foco al campo que falta.
 * - **Cerrar grabando no cierra**: frena la grabación, la conserva y avisa. Un segundo Escape sí
 *   cierra. Nada de `confirm()`.
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

import FeedbackService from './FeedbackService';
import {FeedbackTypeChips} from './FeedbackTypeChips';
import {FeedbackIaBugCard} from './FeedbackIaBugCard';
import {FeedbackConsultaCard} from './FeedbackConsultaCard';
import {FeedbackAudioRecorder} from './FeedbackAudioRecorder';
import {FeedbackAttachments} from './FeedbackAttachments';
import {FeedbackContextNotice} from './FeedbackContextNotice';
import {addFiles} from './feedbackFiles';
import {collectFeedbackContext} from './feedbackContext';
import {fetchFeedbackConfig, submitFeedback} from './feedbackApi';
import {errorKeyForResponse, shouldNudgeForMoreDetail, validateSubmission} from './feedbackValidation';
import {MAX_AUDIO_SECONDS} from './feedbackAudio';
import {useAudioRecorder, STATUS_RECORDING} from './useAudioRecorder';

const STATE_EDITING = 'editing';
const STATE_SUBMITTING = 'submitting';
const STATE_SENT = 'sent';

const DRAFT_DEBOUNCE_MS = 500;
const SENT_AUTOCLOSE_MS = 4000;

function newDedupKey() {
    // uuid4 generado al ABRIR el formulario, no al enviar: así el reintento de un envío que falló
    // por red trae la misma clave y el backend lo reconoce como el mismo feedback.
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `fb-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const FeedbackPanel = ({initialType, sessionKey, uiVersion, experienceTier, onClose}) => {
    const {t} = useTranslation();

    const savedDraft = useMemo(() => FeedbackService.readDraft(sessionKey), [sessionKey]);
    const restoredFromDraft = FeedbackService.hasDraftContent(savedDraft);

    const [type, setType] = useState(initialType || (savedDraft && savedDraft.type) || null);
    const [text, setText] = useState((savedDraft && savedDraft.text) || '');
    const [aiResponse, setAiResponse] = useState((savedDraft && savedDraft.aiResponse) || '');
    const [includeContext, setIncludeContext] = useState(
        savedDraft ? savedDraft.includeContext !== false : true);
    const [draftNoticeVisible, setDraftNoticeVisible] = useState(restoredFromDraft);

    const [attachments, setAttachments] = useState(() => FeedbackService.media().attachments);
    const [audio, setAudio] = useState(() => FeedbackService.media().audio);
    const [rejected, setRejected] = useState([]);

    const [panelState, setPanelState] = useState(STATE_EDITING);
    const [errorKey, setErrorKey] = useState(null);
    const [config, setConfig] = useState(null);
    const [closeBlockedNotice, setCloseBlockedNotice] = useState(false);

    const titleRef = useRef(null);
    const textRef = useRef(null);
    const chipsRef = useRef(null);
    const dedupKeyRef = useRef(newDedupKey());
    const submittingRef = useRef(false);
    const titleId = 'dashbo-feedback-title';

    const onAudioReady = useCallback((recordedAudio) => {
        FeedbackService.setAudio(recordedAudio);
        setAudio(recordedAudio);
    }, []);

    const audioMaxSeconds = (config && config.audio_max_seconds) || MAX_AUDIO_SECONDS;
    const recorder = useAudioRecorder({maxSeconds: audioMaxSeconds, onReady: onAudioReady});

    const context = useMemo(
        () => collectFeedbackContext({uiVersion, experienceTier}),
        [uiVersion, experienceTier]);

    // --- config remota (perezosa: la mayoría de las sesiones nunca abren el panel) --- //
    useEffect(() => {
        let cancelled = false;
        fetchFeedbackConfig().then((response) => {
            if (!cancelled && response && response.status === 'OK') setConfig(response);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    // --- foco al abrir: al TÍTULO, no al primer chip (que se lea de qué se trata antes de elegir) --- //
    useEffect(() => {
        if (titleRef.current) titleRef.current.focus();
    }, []);

    // Marca el body mientras el panel está abierto, para que los banners de la app (free trial,
    // pago suspendido, credenciales caídas) corran su contenido y no queden tapados por el drawer.
    //
    // Se hace por desplazamiento y no por z-index porque el z-index NO tiene solución: en el marco
    // clásico los banners están en 999/1000 y en la 2.1 el repo se los baja a 90 a propósito, para
    // que el topbar (100) siempre les gane. Un único valor para el panel no puede quedar debajo de
    // 999 y debajo de 90 sin hundirse por detrás de todo el contenido. Y subir los banners por
    // encima del panel sería peor: son `top: 0`, así que taparían el header del panel — incluida
    // la X de cerrar.
    useEffect(() => {
        document.body.classList.add('feedback-panel-open');
        return () => document.body.classList.remove('feedback-panel-open');
    }, []);

    // --- borrador (sólo texto: los blobs no entran en localStorage) --- //
    useEffect(() => {
        if (panelState !== STATE_EDITING) return undefined;
        const timer = setTimeout(
            () => FeedbackService.saveDraft({type, text, aiResponse, includeContext}, sessionKey),
            DRAFT_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [type, text, aiResponse, includeContext, panelState, sessionKey]);

    const requestClose = useCallback(() => {
        if (submittingRef.current) return;
        if (recorder.status === STATUS_RECORDING) {
            // No cierra: frena la grabación y la conserva. Cerrar acá tiraría lo que el usuario
            // acaba de decir.
            recorder.stop();
            setCloseBlockedNotice(true);
            return;
        }
        onClose();
    }, [onClose, recorder]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') requestClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [requestClose]);

    // --- paste de imágenes en cualquier parte del panel --- //
    const handlePaste = (event) => {
        const items = Array.from((event.clipboardData && event.clipboardData.items) || []);
        const images = items.filter((item) => item.type && item.type.startsWith('image/'));
        if (!images.length) return;

        const files = images.map((item) => item.getAsFile()).filter(Boolean);
        if (!files.length) return;

        // NO se hace `preventDefault()` a ciegas: copiar de Excel o Word deja imagen Y texto en el
        // clipboard, y matar el paste de texto con el foco en el textarea sería una regresión.
        const hasText = items.some((item) => item.type === 'text/plain');
        const focusInText = document.activeElement
            && ['TEXTAREA', 'INPUT'].includes(document.activeElement.tagName);
        if (!hasText || !focusInText) event.preventDefault();

        handleAddFiles(files);
    };

    const handleAddFiles = async (files) => {
        // `addFiles` normaliza cada imagen con un canvas, o sea que es asíncrono de verdad. La
        // lista base se lee del SERVICE y no del closure: dos pastes seguidos antes de que
        // resuelva el primero veían los dos el mismo `attachments` viejo y el segundo pisaba al
        // primero — el usuario pegaba dos capturas y se mandaba una.
        const current = FeedbackService.media().attachments;
        const result = await addFiles(current, files, {audioBytes: audio ? audio.bytes : 0});
        if (result.added.length) {
            // Se vuelve a leer del service después del await por el mismo motivo.
            const next = [...FeedbackService.media().attachments, ...result.added];
            FeedbackService.setAttachments(next);
            setAttachments(next);
        }
        setRejected(result.rejected);
    };

    const handleRemoveAttachment = (id) => setAttachments(FeedbackService.removeAttachment(id));

    const handleRemoveAudio = () => {
        FeedbackService.clearAudio();
        setAudio(null);
        recorder.reset();
    };

    const startOver = () => {
        // Sólo lo de ESTA sesión: `resetForNewSession` barre los borradores de todas, que es lo
        // correcto al cambiar de usuario pero excesivo para un "Empezar de nuevo".
        FeedbackService.resetMedia();
        FeedbackService.clearDraft(sessionKey);
        setType(null);
        setText('');
        setAiResponse('');
        setAttachments([]);
        setAudio(null);
        setRejected([]);
        setDraftNoticeVisible(false);
        dedupKeyRef.current = newDedupKey();
    };

    // --- envío --- //
    const handleSubmit = async () => {
        if (submittingRef.current) return;

        const validation = validateSubmission({
            type, text, hasAudio: !!audio, attachmentsCount: attachments.length});
        if (!validation.ok) {
            setErrorKey(validation.errorKey);
            const target = validation.focusTarget === 'type' ? chipsRef.current : textRef.current;
            if (target) {
                target.scrollIntoView({behavior: 'smooth', block: 'nearest'});
                if (target.focus) target.focus();
            }
            return;
        }

        submittingRef.current = true;
        setPanelState(STATE_SUBMITTING);
        setErrorKey(null);

        const response = await submitFeedback({
            type,
            text,
            aiResponse: type === 'ia_bug' ? aiResponse : '',
            promptVersion: config ? config.ia_bug_prompt_version : '',
            includeContext,
            context,
            attachments,
            audio,
            dedupKey: dedupKeyRef.current,
        });

        submittingRef.current = false;

        if (response.status !== 'OK') {
            // Vuelve a `editing` con TODO intacto: texto, chips, audio y capturas. Un error de red
            // no puede costarle al usuario lo que escribió.
            setPanelState(STATE_EDITING);
            setErrorKey(errorKeyForResponse(response));
            return;
        }

        FeedbackService.clearDraft(sessionKey);
        FeedbackService.resetMedia();
        setPanelState(STATE_SENT);
        // Gotcha de react-toastify: un `toast()` con un `toastId` ya activo se descarta EN
        // SILENCIO. Por eso el update explícito.
        const toastId = 'feedback-sent';
        if (toast.isActive(toastId)) toast.update(toastId, {render: t('feedback.sent_toast')});
        else toast.success(t('feedback.sent_toast'), {toastId});
    };

    // Auto-cierre del estado enviado, cancelable con el mouse encima o el foco adentro.
    const stickyRef = useRef(false);
    useEffect(() => {
        if (panelState !== STATE_SENT) return undefined;
        const timer = setTimeout(() => {
            if (!stickyRef.current) onClose();
        }, SENT_AUTOCLOSE_MS);
        return () => clearTimeout(timer);
    }, [panelState, onClose]);

    const showNudge = shouldNudgeForMoreDetail(
        {text, hasAudio: !!audio, attachmentsCount: attachments.length});

    const panel = (
        <aside
            id="dashbo-feedback-panel"
            className="feedback-panel"
            role="dialog"
            aria-labelledby={titleId}
            onPaste={handlePaste}
            onMouseEnter={() => {
                stickyRef.current = true;
            }}
            onMouseLeave={() => {
                stickyRef.current = false;
            }}
            onFocusCapture={() => {
                stickyRef.current = true;
            }}
        >
            <header className="feedback-panel__head">
                <div>
                    <h2 id={titleId} tabIndex={-1} ref={titleRef}>{t('feedback.title')}</h2>
                    <p className="feedback-panel__sub">{t('feedback.subtitle')}</p>
                </div>
                <button
                    type="button"
                    className="feedback-panel__close"
                    onClick={requestClose}
                    disabled={panelState === STATE_SUBMITTING}
                    aria-label={t('feedback.close')}
                >
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path d="M6 6l12 12M18 6L6 18"/>
                    </svg>
                </button>
            </header>

            {panelState === STATE_SENT ? (
                <div className="feedback-panel__body -sent">
                    <p className="feedback-sent__title">{t('feedback.sent.title')}</p>
                    <p className="feedback-sent__body">{t('feedback.sent.body')}</p>
                    <div className="feedback-sent__actions">
                        <button type="button" onClick={() => {
                            startOver();
                            setPanelState(STATE_EDITING);
                        }}>
                            {t('feedback.sent.another')}
                        </button>
                        <button type="button" onClick={onClose}>{t('feedback.sent.close')}</button>
                    </div>
                </div>
            ) : (
                <div className="feedback-panel__body">
                    {draftNoticeVisible && (
                        <p className="feedback-draft-notice">
                            {t('feedback.draft.restored')}
                            <button type="button" onClick={startOver}>
                                {t('feedback.draft.discard')}
                            </button>
                        </p>
                    )}

                    <div ref={chipsRef} tabIndex={-1}>
                        <p className="feedback-label">{t('feedback.type_question')}</p>
                        <FeedbackTypeChips selected={type} onSelect={setType} labelledBy={titleId}/>
                    </div>

                    {type === 'ia_bug' && (
                        <FeedbackIaBugCard
                            prompt={config ? config.ia_bug_prompt : ''}
                            aiResponse={aiResponse}
                            onAiResponseChange={setAiResponse}
                        />
                    )}
                    {type === 'consulta' && (
                        <FeedbackConsultaCard
                            whatsappUrl={config ? config.whatsapp_url : ''}
                            whatsappMessage={config ? config.whatsapp_message : ''}
                            screenPath={context.path}
                        />
                    )}

                    <div>
                        <p className="feedback-label">{t('feedback.text_label')}</p>
                        <textarea
                            ref={textRef}
                            value={text}
                            onChange={(event) => setText(event.target.value)}
                            placeholder={t('feedback.text_placeholder')}
                        />
                        {showNudge && <p className="feedback-hint">{t('feedback.text_nudge')}</p>}
                    </div>

                    <div>
                        <p className="feedback-label">{t('feedback.audio_label')}</p>
                        <FeedbackAudioRecorder
                            recorder={recorder}
                            audio={audio}
                            onRemove={handleRemoveAudio}
                            maxSeconds={audioMaxSeconds}
                        />
                    </div>

                    <div>
                        <p className="feedback-label">{t('feedback.attach_label')}</p>
                        <FeedbackAttachments
                            attachments={attachments}
                            rejected={rejected}
                            onAdd={handleAddFiles}
                            onRemove={handleRemoveAttachment}
                        />
                    </div>

                    <FeedbackContextNotice
                        checked={includeContext}
                        onChange={setIncludeContext}
                        context={context}
                    />
                </div>
            )}

            {panelState !== STATE_SENT && (
                <footer className="feedback-panel__foot">
                    {closeBlockedNotice && (
                        <p className="feedback-notice" role="status">
                            {t('feedback.audio.stopped_on_close')}
                        </p>
                    )}
                    {errorKey && <p className="feedback-error" role="alert">{t(errorKey)}</p>}
                    <button
                        type="button"
                        className="feedback-submit"
                        onClick={handleSubmit}
                        disabled={panelState === STATE_SUBMITTING}
                        aria-busy={panelState === STATE_SUBMITTING}
                    >
                        {panelState === STATE_SUBMITTING
                            ? t('feedback.submitting')
                            : t('feedback.submit')}
                    </button>
                    <p className="feedback-foot-note">{t('feedback.footer_note')}</p>
                </footer>
            )}
        </aside>
    );

    // Portal a `document.body`: así el orden del DOM coincide con el orden visual para la
    // navegación por teclado, sin depender de dónde esté montado el launcher.
    return createPortal(panel, document.body);
};

export default FeedbackPanel;
